import { createStore, type StoreApi } from 'zustand/vanilla';
import type { AccountValuation, ApiClient, HubEvent, HubQuote, HubState, Resync } from '@adiox/api';
import { applyEvent, applySnapshot, applyValuation, emptyAccount, type AccountState } from './accountModel';
import { applyQuotes, type QuoteView } from './quoteModel';

/** The parts of the hub client the store uses (a fake stands in for it in tests). */
export interface HubLike {
  on(name: 'quotes', handler: (quotes: HubQuote[]) => void): () => void;
  on(name: 'account', handler: (valuation: AccountValuation) => void): () => void;
  on(name: 'event', handler: (event: HubEvent) => void): () => void;
  on(name: 'feed', handler: (up: boolean) => void): () => void;
  on(name: 'resync', handler: (resync: Resync) => void): () => void;
  on(name: 'state', handler: (state: HubState) => void): () => void;
  start(): Promise<void>;
  stop(): Promise<void>;
  subscribeQuotes(symbols: string[]): Promise<void>;
  unsubscribeQuotes(symbols: string[]): Promise<void>;
  watchAccount(login: number): Promise<void>;
  unwatchAccount(login: number): Promise<void>;
}

export interface LoggedEvent {
  seq: number;
  event: HubEvent['event'];
  receivedAt: number;
}

export interface StreamState {
  hubState: HubState;
  /** Whether the price feed is up (null until known). While it is down quotes are stale and trading is refused. */
  feedUp: boolean | null;
  quotes: Record<string, QuoteView>;
  accounts: Record<number, AccountState>;
  /** Accounts whose snapshot failed to load; retried automatically. */
  loadErrors: Record<number, string>;
  /** The newest hub events (deals, stop-outs, ...), for notifications. */
  recentEvents: LoggedEvent[];
}

export type StreamStore = StoreApi<StreamState> & {
  watchAccount(login: number): Promise<void>;
  unwatchAccount(login: number): Promise<void>;
  subscribeQuotes(symbols: string[]): Promise<void>;
  unsubscribeQuotes(symbols: string[]): Promise<void>;
  reloadAccount(login: number): Promise<void>;
  dispose(): void;
};

const MAX_EVENTS = 200;
const RETRY_MS = 3_000;

/**
 * The live state of the terminal: quotes, feed and connection state, and per-account positions, orders and figures.
 *
 * Loading an account is ordered so no event can be missed: first the watch is confirmed by the server, then the REST snapshot
 * is fetched, then the events that arrived in the meantime (buffered) are applied on top in order. Because events carry whole
 * records, applying an event the snapshot already contains is harmless.
 */
export function createStreamStore(
  hub: HubLike,
  api: ApiClient,
  options: { retryMs?: number } = {},
): StreamStore {
  const store = createStore<StreamState>(() => ({
    hubState: 'disconnected',
    feedUp: null,
    quotes: {},
    accounts: {},
    loadErrors: {},
    recentEvents: [],
  }));
  const retryMs = options.retryMs ?? RETRY_MS;
  const quoteRefs = new Map<string, number>();
  const buffers = new Map<number, HubEvent[]>();
  const pendingValuation = new Map<number, AccountValuation>();
  const loading = new Map<number, Promise<void>>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let disposed = false;

  const patchAccount = (login: number, update: (a: AccountState) => AccountState) =>
    store.setState((s) => {
      const current = s.accounts[login];
      return current ? { accounts: { ...s.accounts, [login]: update(current) } } : s;
    });

  const load = async (login: number): Promise<void> => {
    buffers.set(login, []);
    store.setState((s) => ({
      accounts: { ...s.accounts, [login]: { ...(s.accounts[login] ?? emptyAccount(login)), loaded: false } },
    }));
    try {
      await hub.watchAccount(login); // the server confirmed: from now on every event reaches us
      if (disposed) return;
      const { data, error } = await api.GET('/api/v1/accounts/{login}', { params: { path: { login } } });
      if (disposed) return;
      if (!data) throw new Error(JSON.stringify(error));
      const buffered = buffers.get(login) ?? [];
      buffers.delete(login);
      store.setState((s) => {
        let state = applySnapshot(s.accounts[login] ?? emptyAccount(login), data);
        for (const message of buffered) state = applyEvent(state, message);
        const valuation = pendingValuation.get(login);
        if (valuation) state = applyValuation(state, valuation);
        pendingValuation.delete(login);
        const { [login]: _ok, ...errors } = s.loadErrors;
        return { accounts: { ...s.accounts, [login]: { ...state, loaded: true } }, loadErrors: errors };
      });
    } catch (e) {
      if (disposed) return;
      store.setState((s) => ({
        loadErrors: { ...s.loadErrors, [login]: e instanceof Error ? e.message : 'load failed' },
      }));
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (!disposed && store.getState().accounts[login]) void reload(login);
      }, retryMs);
      timers.add(timer);
    }
  };

  const reload = (login: number): Promise<void> => {
    const existing = loading.get(login);
    if (existing) return existing;
    const run = load(login).finally(() => loading.delete(login));
    loading.set(login, run);
    return run;
  };

  const unsubscribers = [
    hub.on('quotes', (batch) => store.setState((s) => ({ quotes: applyQuotes(s.quotes, batch) }))),
    hub.on('feed', (up) => store.setState({ feedUp: up })),
    hub.on('state', (hubState) => store.setState({ hubState })),
    hub.on('account', (valuation) => {
      const account = store.getState().accounts[valuation.login];
      if (!account) return;
      if (account.loaded) patchAccount(valuation.login, (a) => applyValuation(a, valuation));
      else pendingValuation.set(valuation.login, valuation); // applied right after the snapshot
    }),
    hub.on('event', (message) => {
      const login = message.event.login;
      store.setState((s) => ({
        recentEvents: [
          ...s.recentEvents,
          { seq: message.seq, event: message.event, receivedAt: Date.now() },
        ].slice(-MAX_EVENTS),
      }));
      const account = store.getState().accounts[login];
      if (!account) return;
      if (account.loaded) patchAccount(login, (a) => applyEvent(a, message));
      else buffers.get(login)?.push(message);
    }),
    hub.on('resync', (resync) => {
      const logins =
        resync.login === null ? Object.keys(store.getState().accounts).map(Number) : [resync.login];
      logins.forEach((login) => void reload(login));
    }),
  ];

  return Object.assign(store, {
    watchAccount: (login: number) => reload(login),
    async unwatchAccount(login: number) {
      buffers.delete(login);
      pendingValuation.delete(login);
      store.setState((s) => {
        const { [login]: _gone, ...accounts } = s.accounts;
        return { accounts };
      });
      await hub.unwatchAccount(login);
    },
    // Several panels (Market Watch, charts) may follow the same symbol: the server stream is joined by the first and left by the last.
    subscribeQuotes(symbols: string[]) {
      const first = symbols.filter((s) => {
        const n = quoteRefs.get(s) ?? 0;
        quoteRefs.set(s, n + 1);
        return n === 0;
      });
      return first.length > 0 ? hub.subscribeQuotes(first) : Promise.resolve();
    },
    unsubscribeQuotes(symbols: string[]) {
      const last = symbols.filter((s) => {
        const n = (quoteRefs.get(s) ?? 0) - 1;
        if (n > 0) quoteRefs.set(s, n);
        else quoteRefs.delete(s);
        return n <= 0;
      });
      return last.length > 0 ? hub.unsubscribeQuotes(last) : Promise.resolve();
    },
    reloadAccount: reload,
    dispose() {
      disposed = true;
      unsubscribers.forEach((u) => u());
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    },
  });
}
